/**
 * CharactersPanel - An embedded panel displaying the character collection
 * Similar to CharacterCollectionView but as a full-page content area, not a modal
 */

interface Character {
  id: string;
  name: string;
  imageUrl?: string;
}

import { CharacterGenerationCard } from './CharacterGenerationCard';

type ViewMode = 'loadout' | 'vote' | 'create';

export class CharactersPanel {
  private container: HTMLElement;
  private element: HTMLElement | null = null;
  private characters: Character[] = [];
  private isLoading = false;
  private lightbox: HTMLElement | null = null;
  private lightboxImage: HTMLImageElement | null = null;
  private viewMode: ViewMode = 'loadout';
  private votedCardId: string | null = null;
  private voteCounts: Map<string, number> = new Map();
  private generationCard: CharacterGenerationCard | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.createElement();
    this.hide();
  }

  private createElement(): void {
    this.element = document.createElement('div');
    this.element.id = 'characters-panel';
    this.element.innerHTML = `
      <style>
        #characters-panel {
          position: fixed;
          inset: 0;
          background: rgba(10, 12, 18, 0.98);
          z-index: 9000;
          font-family: system-ui, -apple-system, sans-serif;
          color: white;
          overflow-y: auto;
          padding-bottom: 80px;
        }

        .cp-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 24px;
        }

        .cp-header {
          margin-bottom: 16px;
        }

        .cp-title {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .cp-subtitle {
          font-size: 14px;
          opacity: 0.7;
        }

        .cp-vote-btn {
          display: block;
          width: 100%;
          padding: 16px 24px;
          margin-bottom: 24px;
          border: none;
          border-radius: 12px;
          background: #22c55e;
          color: white;
          font-size: 18px;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 0.2s, transform 0.1s;
        }

        .cp-vote-btn:hover {
          background: #16a34a;
        }

        .cp-vote-btn:active {
          transform: scale(0.98);
        }

        .cp-create-btn {
          display: block;
          width: 100%;
          padding: 16px 24px;
          margin-bottom: 12px;
          border: 2px solid #3b82f6;
          border-radius: 12px;
          background: transparent;
          color: #3b82f6;
          font-size: 18px;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 0.2s, transform 0.1s;
        }

        .cp-create-btn:hover {
          background: rgba(59, 130, 246, 0.1);
        }

        .cp-create-btn:active {
          transform: scale(0.98);
        }

        .cp-creation-view {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
        }

        .cp-creation-header {
          text-align: center;
        }

        .cp-creation-title {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .cp-creation-subtitle {
          font-size: 14px;
          opacity: 0.7;
        }

        .cp-creation-card-container {
          display: flex;
          justify-content: center;
        }

        .cp-file-input-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .cp-file-input {
          display: none;
        }

        .cp-file-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border: 2px dashed rgba(255, 255, 255, 0.3);
          border-radius: 8px;
          background: transparent;
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: border-color 0.2s, background-color 0.2s;
        }

        .cp-file-label:hover {
          border-color: rgba(255, 255, 255, 0.5);
          background: rgba(255, 255, 255, 0.05);
        }

        .cp-selected-files {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.7);
        }

        .cp-generate-btn {
          padding: 14px 32px;
          border: none;
          border-radius: 8px;
          background: #22c55e;
          color: white;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 0.2s, transform 0.1s, opacity 0.2s;
        }

        .cp-generate-btn:hover:not(:disabled) {
          background: #16a34a;
        }

        .cp-generate-btn:active:not(:disabled) {
          transform: scale(0.98);
        }

        .cp-generate-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .cp-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          background: transparent;
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .cp-back-btn:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .cp-tabs {
          display: flex;
          gap: 4px;
          margin-bottom: 24px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          padding: 4px;
        }

        .cp-tab {
          flex: 1;
          padding: 12px 16px;
          border: none;
          border-radius: 8px;
          background: transparent;
          color: rgba(255, 255, 255, 0.6);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s, color 0.2s;
        }

        .cp-tab:hover {
          background: rgba(255, 255, 255, 0.05);
          color: rgba(255, 255, 255, 0.8);
        }

        .cp-tab--active {
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }

        .cp-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 16px;
        }

        .cp-card {
          border: 2px solid transparent;
          border-radius: 12px;
          padding: 14px;
          background: rgba(255, 255, 255, 0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          transition: border-color 0.2s, background-color 0.2s, transform 0.2s;
        }

        .cp-card:hover {
          background: rgba(255, 255, 255, 0.12);
          transform: translateY(-2px);
        }

        .cp-card--faded {
          opacity: 0.25;
        }

        .cp-card--faded .cp-card-label {
          color: rgba(255, 255, 255, 0.5);
        }

        .cp-card-image {
          width: 160px;
          height: 240px;
          border-radius: 8px;
          object-fit: cover;
          background: rgba(30, 30, 35, 0.8);
        }

        .cp-card-label {
          font-size: 13px;
          font-weight: 600;
          text-align: center;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .cp-card-votes {
          font-size: 12px;
          color: #22c55e;
          font-weight: 700;
        }

        .cp-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          gap: 16px;
        }

        .cp-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.1);
          border-top-color: #22c55e;
          border-radius: 50%;
          animation: cp-spin 1s linear infinite;
        }

        @keyframes cp-spin {
          to { transform: rotate(360deg); }
        }

        .cp-loading-text {
          font-size: 14px;
          opacity: 0.7;
        }

        .cp-empty {
          text-align: center;
          padding: 60px 20px;
          opacity: 0.6;
        }

        .cp-empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .cp-lightbox {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
          cursor: pointer;
        }

        .cp-lightbox-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          max-height: 90dvh;
        }

        .cp-lightbox-image {
          max-width: 90vw;
          max-height: 90vh;
          max-height: 90dvh;
          border-radius: 12px;
          object-fit: contain;
        }

        .cp-lightbox-close {
          position: absolute;
          top: -40px;
          right: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.2);
          color: white;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
        }

        .cp-lightbox-close:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .cp-voting-title {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .cp-voting-subtitle {
          font-size: 14px;
          opacity: 0.7;
          margin-bottom: 24px;
        }

        .cp-card-vote-btn {
          width: 100%;
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          background: #3b82f6;
          color: white;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s, opacity 0.2s;
        }

        .cp-card-vote-btn:hover:not(:disabled) {
          background: #2563eb;
        }

        .cp-card-vote-btn--voted {
          background: #22c55e;
          cursor: default;
        }

        .cp-card-vote-btn--voted:hover {
          background: #22c55e;
        }

        .cp-card-vote-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      </style>

      <div class="cp-container">
        <div class="cp-header">
          <div class="cp-tabs">
            <button class="cp-tab cp-tab--active" data-tab="loadout">Loadout</button>
            <button class="cp-tab" data-tab="vote">Vote</button>
            <button class="cp-tab" data-tab="create">Create</button>
          </div>
        </div>
        <div class="cp-content"></div>
      </div>
    `;

    this.container.appendChild(this.element);

    // Create lightbox
    this.lightbox = document.createElement('div');
    this.lightbox.className = 'cp-lightbox';
    this.lightbox.style.display = 'none';
    this.lightbox.addEventListener('click', (e) => {
      if (e.target === this.lightbox) {
        this.hideLightbox();
      }
    });

    const lightboxContent = document.createElement('div');
    lightboxContent.className = 'cp-lightbox-content';

    this.lightboxImage = document.createElement('img');
    this.lightboxImage.className = 'cp-lightbox-image';
    lightboxContent.appendChild(this.lightboxImage);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'cp-lightbox-close';
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => this.hideLightbox());
    lightboxContent.appendChild(closeBtn);

    this.lightbox.appendChild(lightboxContent);

    // Setup tab listeners
    this.setupTabListeners();
  }

  private setupTabListeners(): void {
    const tabs = this.element?.querySelectorAll('.cp-tab');
    tabs?.forEach(tab => {
      tab.addEventListener('click', () => {
        const newMode = tab.getAttribute('data-tab') as ViewMode;
        if (newMode && newMode !== this.viewMode) {
          // Cleanup if leaving create view
          if (this.viewMode === 'create') {
            this.cleanupGenerationCard();
          }
          this.viewMode = newMode;
          this.updateTabActiveState();
          this.renderContent();
        }
      });
    });
  }

  private updateTabActiveState(): void {
    const tabs = this.element?.querySelectorAll('.cp-tab');
    tabs?.forEach(tab => {
      const tabMode = tab.getAttribute('data-tab');
      if (tabMode === this.viewMode) {
        tab.classList.add('cp-tab--active');
      } else {
        tab.classList.remove('cp-tab--active');
      }
    });
  }

  private showLightbox(imageUrl: string): void {
    if (!this.lightboxImage || !this.lightbox) return;
    this.lightboxImage.src = imageUrl;
    if (!this.lightbox.parentNode) {
      document.body.appendChild(this.lightbox);
    }
    this.lightbox.style.display = 'flex';
  }

  private hideLightbox(): void {
    if (this.lightbox) {
      this.lightbox.style.display = 'none';
    }
  }

  async fetchCharacters(): Promise<void> {
    this.isLoading = true;
    this.renderContent();

    try {
      const response = await fetch('https://ajcb6tx6g3.eu-central-1.awsapprunner.com/api/generations');
      if (response.ok) {
        const data = await response.json();
        this.characters = (data.generations || []).map((gen: { id: string; prompt?: string; image_url?: string }) => ({
          id: gen.id,
          name: gen.prompt?.slice(0, 30) || 'Character',
          imageUrl: gen.image_url
        }));
      }
    } catch (err) {
      console.error('Failed to fetch characters:', err);
    }

    this.isLoading = false;
    this.renderContent();
  }

  private getCollectionCharacters(): Character[] {
    // First 20 characters for the main collection view
    return this.characters.slice(0, 20);
  }

  private getVotingCharacters(): Character[] {
    // Last 10 characters for the voting view
    return this.characters.slice(-10);
  }

  private calculateVotes(index: number): number {
    // (10 - index)^2 + random(0-10)
    const base = Math.pow(10 - index, 2);
    const random = Math.floor(Math.random() * 11);
    return base + random;
  }

  private renderContent(): void {
    const content = this.element?.querySelector('.cp-content');
    if (!content) return;

    if (this.isLoading) {
      content.innerHTML = `
        <div class="cp-loading">
          <div class="cp-spinner"></div>
          <div class="cp-loading-text">Loading characters...</div>
        </div>
      `;
      return;
    }

    if (this.characters.length === 0) {
      content.innerHTML = `
        <div class="cp-empty">
          <div class="cp-empty-icon">📭</div>
          <div>No characters collected yet</div>
        </div>
      `;
      return;
    }

    if (this.viewMode === 'vote') {
      this.renderVotingView(content);
    } else if (this.viewMode === 'create') {
      this.renderCreationView(content);
    } else {
      this.renderLoadoutView(content);
    }
  }

  private renderLoadoutView(content: Element): void {
    const collectionCharacters = this.getCollectionCharacters();
    const halfIndex = Math.ceil(collectionCharacters.length / 2);

    let html = '<div class="cp-grid">';

    collectionCharacters.forEach((char, index) => {
      const isFaded = index >= halfIndex;
      html += `
        <div class="cp-card ${isFaded ? 'cp-card--faded' : ''}" data-id="${char.id}" data-image="${char.imageUrl || ''}">
          ${char.imageUrl
            ? `<img class="cp-card-image" src="${char.imageUrl}" alt="${char.name}">`
            : `<div class="cp-card-image" style="display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,0.3);font-size:12px;">No image</div>`
          }
          <div class="cp-card-label">${char.name}</div>
        </div>
      `;
    });
    html += '</div>';

    content.innerHTML = html;

    // Setup card click listeners for lightbox
    this.setupCardListeners(content);
  }

  private renderVotingView(content: Element): void {
    const votingCharacters = this.getVotingCharacters();

    // Initialize vote counts if not already set
    votingCharacters.forEach((char, index) => {
      if (!this.voteCounts.has(char.id)) {
        this.voteCounts.set(char.id, this.calculateVotes(index));
      }
    });

    let html = `
      <div class="cp-voting-title">Vote for the Next Character</div>
      <div class="cp-voting-subtitle">Select which character should be added next</div>
      <div class="cp-grid">
    `;

    votingCharacters.forEach((char) => {
      const votes = this.voteCounts.get(char.id) || 0;
      const isVoted = this.votedCardId === char.id;
      const isDisabled = this.votedCardId !== null && !isVoted;

      let btnClass = 'cp-card-vote-btn';
      if (isVoted) btnClass += ' cp-card-vote-btn--voted';

      const btnText = isVoted ? 'Voted' : 'Vote';
      const btnDisabled = isDisabled ? 'disabled' : '';

      html += `
        <div class="cp-card" data-id="${char.id}" data-image="${char.imageUrl || ''}">
          ${char.imageUrl
            ? `<img class="cp-card-image" src="${char.imageUrl}" alt="${char.name}">`
            : `<div class="cp-card-image" style="display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,0.3);font-size:12px;">No image</div>`
          }
          <div class="cp-card-label">${char.name}</div>
          <div class="cp-card-votes" data-votes-id="${char.id}">${votes} votes</div>
          <button class="${btnClass}" data-vote-id="${char.id}" ${btnDisabled}>${btnText}</button>
        </div>
      `;
    });
    html += '</div>';

    content.innerHTML = html;

    // Setup vote button listeners
    this.setupVoteButtonListeners(content);

    // Setup card click listeners for lightbox
    this.setupCardListeners(content);
  }

  private renderCreationView(content: Element): void {
    content.innerHTML = `
      <div class="cp-creation-view">
        <div class="cp-creation-header">
          <div class="cp-creation-title">Create New Character</div>
          <div class="cp-creation-subtitle">Upload reference images to generate a character</div>
        </div>
        <div class="cp-creation-card-container" id="cp-card-container"></div>
        <div class="cp-file-input-container">
          <input type="file" id="cp-file-input" class="cp-file-input" accept="image/*" multiple>
          <label for="cp-file-input" class="cp-file-label">
            Select images
          </label>
          <div class="cp-selected-files" id="cp-selected-files"></div>
          <button class="cp-generate-btn" id="cp-generate-btn" disabled>Generate Character</button>
        </div>
      </div>
    `;

    // Create and mount the generation card
    const cardContainer = content.querySelector('#cp-card-container');
    if (cardContainer) {
      this.generationCard = new CharacterGenerationCard({
        onComplete: (result: { imageUrl: string }) => {
          // After completion, allow user to go back or stay viewing the result
          console.log('Character generated:', result);
        },
        onError: (error: Error) => {
          console.error('Character generation failed:', error);
        }
      });
      cardContainer.appendChild(this.generationCard.getElement());
    }

    // Setup file input
    const fileInput = content.querySelector('#cp-file-input') as HTMLInputElement;
    const selectedFilesEl = content.querySelector('#cp-selected-files');
    const generateBtn = content.querySelector('#cp-generate-btn') as HTMLButtonElement;

    let selectedFiles: FileList | null = null;

    fileInput?.addEventListener('change', () => {
      selectedFiles = fileInput.files;
      if (selectedFiles && selectedFiles.length > 0) {
        selectedFilesEl!.textContent = `${selectedFiles.length} image${selectedFiles.length > 1 ? 's' : ''} selected`;
        generateBtn.disabled = false;
      } else {
        selectedFilesEl!.textContent = '';
        generateBtn.disabled = true;
      }
    });

    generateBtn?.addEventListener('click', () => {
      if (selectedFiles && this.generationCard) {
        generateBtn.disabled = true;
        fileInput.disabled = true;
        this.generationCard.startGeneration(selectedFiles);
      }
    });
  }

  private cleanupGenerationCard(): void {
    if (this.generationCard) {
      this.generationCard.destroy();
      this.generationCard = null;
    }
  }

  private setupVoteButtonListeners(content: Element): void {
    const voteButtons = content.querySelectorAll('.cp-card-vote-btn');
    voteButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent lightbox from opening
        const cardId = btn.getAttribute('data-vote-id');
        if (!cardId) return;

        // Check if user is unvoting (clicking the same card they voted for)
        if (this.votedCardId === cardId) {
          // Unvote
          this.votedCardId = null;

          // Decrement the vote count
          const currentVotes = this.voteCounts.get(cardId) || 0;
          this.voteCounts.set(cardId, Math.max(0, currentVotes - 1));

          // Update the clicked button
          btn.textContent = 'Vote';
          btn.classList.remove('cp-card-vote-btn--voted');

          // Re-enable all other buttons
          voteButtons.forEach(otherBtn => {
            (otherBtn as HTMLButtonElement).disabled = false;
          });

          // Update the vote count display
          const votesDisplay = content.querySelector(`[data-votes-id="${cardId}"]`);
          if (votesDisplay) {
            votesDisplay.textContent = `${Math.max(0, currentVotes - 1)} votes`;
          }
          return;
        }

        // Can't vote if already voted for a different card
        if (this.votedCardId !== null) return;

        // Mark this card as voted
        this.votedCardId = cardId;

        // Increment the vote count
        const currentVotes = this.voteCounts.get(cardId) || 0;
        this.voteCounts.set(cardId, currentVotes + 1);

        // Update the UI
        // Update the clicked button
        btn.textContent = 'Voted';
        btn.classList.add('cp-card-vote-btn--voted');

        // Disable all other buttons
        voteButtons.forEach(otherBtn => {
          if (otherBtn !== btn) {
            (otherBtn as HTMLButtonElement).disabled = true;
          }
        });

        // Update the vote count display
        const votesDisplay = content.querySelector(`[data-votes-id="${cardId}"]`);
        if (votesDisplay) {
          votesDisplay.textContent = `${currentVotes + 1} votes`;
        }
      });
    });
  }

  private setupCardListeners(content: Element): void {
    const cards = content.querySelectorAll('.cp-card');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        const imageUrl = card.getAttribute('data-image');
        if (imageUrl) {
          this.showLightbox(imageUrl);
        }
      });
    });
  }

  show(): void {
    if (this.element) {
      this.element.style.display = 'block';
      this.viewMode = 'loadout';
      this.updateTabActiveState();
      this.votedCardId = null;
      this.voteCounts.clear();
      this.fetchCharacters();
    }
  }

  hide(): void {
    this.cleanupGenerationCard();
    if (this.element) {
      this.element.style.display = 'none';
    }
    this.hideLightbox();
  }

  isVisible(): boolean {
    return this.element?.style.display !== 'none';
  }

  dispose(): void {
    this.cleanupGenerationCard();
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    if (this.lightbox && this.lightbox.parentNode) {
      this.lightbox.parentNode.removeChild(this.lightbox);
    }
    this.element = null;
    this.lightbox = null;
  }
}
